/**
 * Projection screen (/proyeccion/:electionId): what the room sees on the big screen. It follows the
 * election by itself, no need to change URL between rounds:
 *   round open    → turnout + countdown + the QR to vote from the phone (no per-candidate numbers, ADB-139)
 *   round closed  → the round replayed as a bar race, then the result banner
 *   election done → the board of elected positions
 *
 * Operator keys (no buttons on screen): R replay · Space pause/resume · 1-4 speed · B board · T light/dark · Esc automatic.
 * Light or dark theme (old projectors wash out dark screens): button top right or the T key, remembered on the device.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Maximize, Moon, ShieldAlert, Sun } from 'lucide-react';
import { useAuth } from '@/context/authContext';
import { usePermissions } from '@/hooks/usePermissions';
import { getRaceTimeline } from '../data/votingRepository';
import { useBarRacePlayer, type BarRacePlayer } from '../hooks/useBarRacePlayer';
import { useElectionState } from '../hooks/useVoting';
import { BarChartRace } from '../components/BarChartRace';
import { FitToScreen } from '../components/FitToScreen';
import { PositionsBoard } from '../components/PositionsBoard';
import { CheckinCounter } from '../components/CheckinCounter';
import { VotingQr } from '../components/VotingQr';
import { RoundCountdown } from '../components/RoundCountdown';
import type { ElectionState, RaceTimeline, StateRace } from '../types';
import { RACE_SPEEDS, colorIndexOf } from '../utils/barRace';
import { candidateName } from '../utils/rounds';
import { votingErrorMessage } from '../utils/votingErrors';

const REPLAY_MS = 14000;
const THEME_KEY = 'voting-projection-theme';

type Theme = 'dark' | 'light';

function readTheme(): Theme {
  try {
    return localStorage.getItem(THEME_KEY) === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

export function ProjectionPage() {
  const { electionId } = useParams<{ electionId: string }>();
  const { user } = useAuth();
  const location = useLocation();
  const { can, isLoading } = usePermissions();

  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  if (isLoading) return <Screen><Loader2 className="size-10 animate-spin opacity-60" /></Screen>;
  if (!can('election.view')) {
    return (
      <Screen>
        <ShieldAlert className="size-12 opacity-60" />
        <p className="text-xl">Su usuario no tiene permiso para ver esta elección.</p>
        <Link to="/" className="underline">Volver</Link>
      </Screen>
    );
  }
  return <ProjectionScreen electionId={electionId as string} />;
}

function Screen({ children }: { children: React.ReactNode }) {
  return (
    <div className="dark flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-950 p-8 text-center text-white">{children}</div>
  );
}

function ProjectionScreen({ electionId }: { electionId: string }) {
  const { data, error, isLoading } = useElectionState(electionId);
  const state = data?.state;
  const fetchedAt = data?.fetchedAt ?? Date.now();
  const [boardOnly, setBoardOnly] = useState(false);
  const [theme, setTheme] = useState<Theme>(readTheme);
  const toggleTheme = () =>
    setTheme((t) => {
      const next: Theme = t === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(THEME_KEY, next);
      } catch {
        // storage unavailable: the choice lasts until reload
      }
      return next;
    });

  const open = useMemo(() => state?.races.find((r) => r.status === 'OPEN') ?? null, [state]);
  const lastClosed = useMemo(
    () =>
      [...(state?.races ?? [])]
        .filter((r) => r.status === 'CLOSED' && r.closed_at)
        .sort((a, b) => new Date(b.closed_at as string).getTime() - new Date(a.closed_at as string).getTime())[0] ?? null,
    [state],
  );
  const showingClosed = !open && !!lastClosed && !boardOnly;

  const { data: timeline } = useQuery({
    queryKey: ['voting', 'timeline', lastClosed?.id],
    queryFn: () => getRaceTimeline(lastClosed?.id as string),
    enabled: showingClosed,
    staleTime: Infinity,
  });

  // The replay clock lives here so the operator keys can drive it
  const player = useBarRacePlayer({ playbackMs: REPLAY_MS, ready: showingClosed && !!timeline, resetKey: lastClosed?.id });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === 'r' || e.key === 'R') player.restart();
      else if (e.key === ' ') {
        e.preventDefault();
        player.toggle();
      } else if (e.key >= '1' && e.key <= '4') player.setSpeed(RACE_SPEEDS[Number(e.key) - 1]);
      else if (e.key === 'b' || e.key === 'B') setBoardOnly((v) => !v);
      else if (e.key === 't' || e.key === 'T') toggleTheme();
      else if (e.key === 'Escape') {
        setBoardOnly(false);
        player.setSpeed(1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [player]);

  if (isLoading) return <Screen><Loader2 className="size-10 animate-spin opacity-60" /></Screen>;
  if (error || !state) return <Screen><p className="text-2xl text-red-400">{votingErrorMessage(error)}</p></Screen>;

  const finished = state.election.status === 'CLOSED' || state.election.status === 'ARCHIVED';
  const hasElected = state.positions.some((p) => p.status === 'FILLED');

  return (
    <div className={`relative min-h-screen ${theme === 'dark' ? 'dark bg-slate-950 text-white' : 'bg-white text-slate-900'}`}>
      <div className="absolute right-4 top-4 z-10 flex items-center gap-2">
        <button
          type="button"
          onClick={toggleTheme}
          className="bg-current/10 flex items-center gap-2 rounded-md px-3 py-1.5 text-sm opacity-30 transition hover:opacity-100"
          aria-label={theme === 'dark' ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'}
        >
          {theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
          {theme === 'dark' ? 'Tema claro' : 'Tema oscuro'}
        </button>
        <FullscreenButton />
      </div>
      <FitToScreen padding={0}>
        <div className="p-6 md:p-10">
          <header className="mb-6">
            <h1 className="text-3xl font-bold opacity-90 md:text-4xl">{state.election.name}</h1>
            {state.election.event_name && <p className="text-xl opacity-60">{state.election.event_name}</p>}
          </header>

          {finished || boardOnly ? (
            <div className="space-y-6">
              <h2 className="text-5xl font-black">{finished ? 'Resultados finales' : 'Oficina elegida'}</h2>
              <PositionsBoard state={state} large title="Cargos" />
            </div>
          ) : open ? (
            <OpenRound state={state} race={open} electionId={electionId} fetchedAt={fetchedAt} showBoard={hasElected} />
          ) : lastClosed ? (
            <ClosedRound state={state} race={lastClosed} timeline={timeline ?? null} player={player} showBoard={hasElected} />
          ) : (
            <div className="grid items-center gap-10 lg:grid-cols-[1fr_auto]">
              <div className="space-y-4">
                <p className="text-6xl font-black">Pronto comenzará la votación</p>
                <p className="text-3xl opacity-70">Escanee el código para dejar listo su teléfono.</p>
                {state.election.attendance_rule === 'CHECKIN' && <CheckinCounter electionId={electionId} large />}
              </div>
              <VotingQr electionId={electionId} size={360} />
            </div>
          )}
        </div>
      </FitToScreen>
      <p className="pointer-events-none fixed bottom-2 left-4 text-xs opacity-0 transition-opacity [@media(hover:hover)]:hover:opacity-40">
        R repetir · Espacio pausa · 1–4 velocidad · B tablero · T tema · Esc automático
      </p>
    </div>
  );
}

function FullscreenButton() {
  const [fs, setFs] = useState(false);
  useEffect(() => {
    const on = () => setFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', on);
    return () => document.removeEventListener('fullscreenchange', on);
  }, []);
  if (fs) return null;
  return (
    <button
      type="button"
      onClick={() => void document.documentElement.requestFullscreen?.()}
      className="bg-current/10 flex items-center gap-2 rounded-md px-3 py-1.5 text-sm opacity-30 transition hover:opacity-100"
    >
      <Maximize className="size-4" /> Pantalla completa
    </button>
  );
}

function OpenRound({ state, race, electionId, fetchedAt, showBoard }: { state: ElectionState; race: StateRace; electionId: string; fetchedAt: number; showBoard: boolean }) {
  const title = state.positions.find((p) => p.id === race.position_id)?.title ?? '';
  const cast = race.votes_cast ?? 0;
  const eligible = race.eligible_now ?? 0;
  const pct = eligible > 0 ? Math.min(100, (cast / eligible) * 100) : 0;
  return (
    <div className="space-y-8">
      <div className="grid items-center gap-10 lg:grid-cols-[1fr_auto]">
        <div className="space-y-6">
          <div>
            <p className="text-3xl opacity-70">Ronda {race.round_number}</p>
            <h2 className="text-7xl font-black leading-tight">{title}</h2>
          </div>
          <div className="flex items-end gap-4">
            <span className="text-[9rem] font-black tabular-nums leading-none text-sky-500 dark:text-sky-400">{cast}</span>
            <span className="pb-4 text-5xl font-bold opacity-60">/ {eligible}</span>
          </div>
          <div className="h-8 w-full max-w-4xl overflow-hidden rounded-full bg-black/10 dark:bg-white/10">
            <div className="h-full rounded-full bg-sky-500 transition-all duration-700 dark:bg-sky-400" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex flex-wrap items-center gap-6 text-3xl opacity-80">
            <span>{pct.toFixed(1)} % ha votado</span>
            {race.closes_at && (
              <span className="text-4xl [&_span]:!text-current"><RoundCountdown closesAt={race.closes_at} serverNow={state.server_now} fetchedAt={fetchedAt} /></span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-center gap-3">
          <VotingQr electionId={electionId} size={340} />
          <p className="max-w-[340px] text-center text-2xl">Escanee para votar desde su teléfono</p>
          <p className="max-w-[340px] text-center text-lg opacity-60">o pida ayuda en el kiosco</p>
        </div>
      </div>
      {showBoard && <PositionsBoard state={state} large title="Cargos" />}
    </div>
  );
}

function ClosedRound({ state, race, timeline, player, showBoard }: { state: ElectionState; race: StateRace; timeline: RaceTimeline | null; player: BarRacePlayer; showBoard: boolean }) {
  const position = state.positions.find((p) => p.id === race.position_id);
  const rd = race.result_data;
  const pool = state.candidates.map((c) => c.id);
  const candidates = (race.candidate_ids ?? []).map((id) => {
    const c = state.candidates.find((x) => x.id === id);
    return { id, name: c?.full_name ?? '—', photo: c?.photo_filename ?? null, colorIndex: colorIndexOf(pool, id) };
  });
  // The banner waits for the replay to end so it does not spoil it
  const revealed = player.finished;
  const winnerName = candidateName(state, race.result_candidate_id);
  const elected = position?.status === 'FILLED' && position.winner_candidate_id === race.result_candidate_id;

  return (
    <div className="space-y-6">
      <div>
        <p className="text-3xl opacity-70">Resultado · Ronda {race.round_number}</p>
        <h2 className="text-6xl font-black">{position?.title}</h2>
      </div>

      {!timeline || !rd ? (
        <Loader2 className="size-10 animate-spin opacity-60" />
      ) : (
        <BarChartRace
          key={race.id}
          large
          controls={false}
          player={player}
          candidates={candidates}
          timeline={timeline}
          baseCount={rd.base_count}
          thresholdPct={Number(race.threshold_pct)}
          winnerId={race.result_candidate_id}
          blankVotes={rd.blank}
        />
      )}

      {revealed && rd && (
        <div className={`rounded-3xl p-6 text-center text-5xl font-black text-white ${race.result === 'WINNER' ? 'bg-green-600' : 'bg-amber-600'}`}>
          {race.result === 'WINNER' && (elected ? `${winnerName}, elegido como ${position?.title}` : `Ganador: ${winnerName}`)}
          {race.result === 'NO_WINNER' && `Nadie alcanzó el ${Number(race.threshold_pct)} % — habrá una nueva ronda`}
          {race.result === 'TIE' && 'Empate — habrá una nueva ronda'}
        </div>
      )}
      {showBoard && <PositionsBoard state={state} large title="Cargos" />}
    </div>
  );
}
